import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Timer, ClipboardList, Gauge, ShieldCheck } from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/Reveal";

const EASE = [0.16, 1, 0.3, 1];
const CHIPS = [
  { icon: ClipboardList, label: "6 questions" },
  { icon: Timer, label: "~5 minutes" },
  { icon: Gauge, label: "Instant score" },
  { icon: ShieldCheck, label: "Zero paperwork" },
];

const SIZE = 220;
const STROKE = 14;
const R = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * R;
const SAMPLE = 78;

const MiniGauge = () => {
  const offset = CIRC * (1 - SAMPLE / 100);
  return (
    <div className="relative mx-auto" style={{ width: SIZE, height: SIZE }}>
      <svg width={SIZE} height={SIZE} className="-rotate-90">
        <circle cx={SIZE / 2} cy={SIZE / 2} r={R} stroke="rgba(255,255,255,0.10)" strokeWidth={STROKE} fill="none" />
        <motion.circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={R}
          stroke="#C89933"
          strokeWidth={STROKE}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={CIRC}
          initial={{ strokeDashoffset: CIRC }}
          whileInView={{ strokeDashoffset: offset }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 1.8, ease: EASE }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-serif-display text-[#FAFAF8] leading-none tabular-nums" style={{ fontSize: 64, letterSpacing: "-0.02em" }}>
          {SAMPLE}
        </span>
        <span className="mt-1 overline text-[0.6rem] text-[#C89933]">out of 100</span>
      </div>
    </div>
  );
};

const DiscoveryTeaser = () => {
  return (
    <section
      id="discovery-teaser"
      className="relative bg-[#0F1E3D] py-24 lg:py-32 overflow-hidden"
      data-testid="discovery-teaser-section"
    >
      <div className="grain-overlay" />
      {/* subtle bronze glow */}
      <div className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#C89933]/10 blur-[120px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-[#C89933]/40 bg-[#C89933]/10 px-3.5 py-1.5 mb-8"
          >
            <Sparkles className="h-3.5 w-3.5 text-[#C89933]" strokeWidth={1.6} />
            <span className="overline text-[0.6rem] text-[#C89933]">
              Client Discovery · 5-Minute Assessment
            </span>
          </motion.div>

          <h2 className="font-serif-display text-[#FAFAF8] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] tracking-tight">
            <MaskedLines
              lines={[
                "Are you truly",
                <span key="italic" className="italic text-[#C89933]">compounding,</span>,
                "or merely accumulating?",
              ]}
            />
          </h2>

          <FadeUp delay={0.25}>
            <p className="mt-8 max-w-xl text-[#FAFAF8]/70 text-base lg:text-lg font-light leading-relaxed">
              Take our confidential 6-question diagnostic and get your personalised
              <span className="text-[#FAFAF8]"> Structural Health Score</span>. A quick, honest read on how thoughtfully your wealth is actually put together.
            </p>
          </FadeUp>

          <FadeUp delay={0.35}>
            <div className="mt-10 flex flex-wrap gap-2.5">
              {CHIPS.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3.5 py-1.5 text-xs text-[#FAFAF8]/85"
                >
                  <Icon className="h-3.5 w-3.5 text-[#C89933]" strokeWidth={1.6} />
                  {label}
                </span>
              ))}
            </div>
          </FadeUp>

          <FadeUp delay={0.45}>
            <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-5">
              <Link
                to="/discovery"
                className="group inline-flex items-center gap-3 rounded-full bg-[#C89933] px-8 py-4 text-sm sm:text-base font-medium text-[#0F1E3D] hover:bg-[#FAFAF8] transition-colors duration-400"
                data-testid="teaser-cta"
              >
                Take the Discovery
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.8} />
              </Link>
              <p className="overline text-[0.62rem] text-[#FAFAF8]/55">
                Confidential · Fiduciary · Zero commissions
              </p>
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1, ease: EASE, delay: 0.15 }}
            className="relative rounded-sm border border-white/10 bg-white/[0.03] px-6 py-10 sm:px-10 sm:py-14 text-center overflow-hidden"
          >
            <p className="overline text-[0.6rem] text-[#C89933]">Sample Preview</p>
            <p className="mt-2 mb-8 text-sm text-[#FAFAF8]/60 font-light">Your Structural Health Score</p>
            <MiniGauge />
            <p className="mt-8 max-w-xs mx-auto text-sm text-[#FAFAF8]/60 font-light">
              A solid foundation, with room to sharpen tax and structure.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DiscoveryTeaser;
